import React, {useState} from 'react';
import {Alert, Col, Row} from 'react-bootstrap';
import MonacoEditor from '@monaco-editor/react';
import {useData} from '../context/ConfigDataContext';
import {useConfigSnapshots} from '../hooks/useConfigSnapshots';
import ConfigTree from './treeView';
import DeviceDefinition from './deviceDefinitions';
import ConfigModeTabs from './ConfigModeTabs';
import ConfigVersionSelector from './ConfigVersionSelector';
import ConfigActionButtons from './ConfigActionButtons';
import ConfigEditorSkeleton from './ConfigEditorSkeleton';
import SaveConfigModal from './SaveConfigModal';
import {sccSchemaPublicV1} from '../../../shared/constants/scc.schema.public.v1';
import '../styles/config.css';

const JsonConfigEditor = ({eid}) => {
  const [configMode, setConfigMode] = useState('tree');
  const [saveModal, setSaveModal] = useState({show: false, mode: null});
  const {currentJsonText, updateCurrentJsonText} = useData();

  const {
    loading,
    error,
    configs,
    activeConfig,
    selectedConfig,
    sgrcontainer,
    save,
    saveAndActivate,
    activate,
    selectConfig,
    hasChanges,
    isSelectedConfigActive,
    isAnyConfigSelected,
    hasActiveConfig,
  } = useConfigSnapshots(eid, updateCurrentJsonText);

  if (loading) return <ConfigEditorSkeleton/>;

  const changed = hasChanges(currentJsonText);
  const selectedActive = isSelectedConfigActive();
  const anySelected = isAnyConfigSelected();
  const hasActive = hasActiveConfig();

  const handleSaveConfirm = (note) => {
    setSaveModal({show: false, mode: null});
    if (saveModal.mode === 'save') save(currentJsonText, note);
    else if (saveModal.mode === 'saveAndActivate') saveAndActivate(currentJsonText, note);
  };

  return (
    <>
      {error && <Alert variant="warning" className="mb-3">{error}</Alert>}
      <Row className="d-flex align-items-center justify-content-between mb-1" style={{width: '100%'}}>
        <Col xs="auto">
          <ConfigModeTabs configMode={configMode} setConfigMode={setConfigMode}/>
        </Col>
        <Col className="d-flex align-items-center justify-content-center gap-2">
          <ConfigVersionSelector
            configs={configs}
            activeConfig={activeConfig}
            selectedConfig={selectedConfig}
            onSelect={selectConfig}
          />
        </Col>
        <Col xs="auto">
          <ConfigActionButtons
            hasChanges={changed}
            hasActiveConfig={hasActive}
            isAnyConfigSelected={anySelected}
            isSelectedConfigActive={selectedActive}
            onReset={() => updateCurrentJsonText(configs[selectedConfig]?.value)}
            onSave={() => setSaveModal({show: true, mode: 'save'})}
            onSaveAndActivate={() => setSaveModal({show: true, mode: 'saveAndActivate'})}
            onActivate={activate}
          />
        </Col>
      </Row>
      <Row style={{width: '100%'}}>
        <Col style={{margin: '10px'}}>
          {configMode === 'tree' && <ConfigTree/>}
          {configMode === 'device' && <DeviceDefinition sgrcontainer={sgrcontainer}/>}
          {configMode === 'json' && (
            <div className="ignore-animation json-editor">
              <MonacoEditor
                height="calc(100vh - 160px)"
                language="json"
                className="json-editor"
                value={currentJsonText}
                onChange={(e) => updateCurrentJsonText(e)}
                editorWillMount={(monaco) => {
                  monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
                    validate: true,
                    schemas: [{
                      uri: 'https://smartchargecontroller.ch/scc.schema.json',
                      fileMatch: ['*'],
                      schema: sccSchemaPublicV1,
                    }],
                  });
                }}
              />
            </div>
          )}
        </Col>
      </Row>
      <SaveConfigModal
        show={saveModal.show}
        title={saveModal.mode === 'saveAndActivate' ? 'Save & Apply' : 'Save Config'}
        onConfirm={handleSaveConfirm}
        onHide={() => setSaveModal({show: false, mode: null})}
      />
    </>
  );
};

export default JsonConfigEditor;
